/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-len */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HotToastService } from '@ngneat/hot-toast';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { JobsService } from 'app/core/services/jobs/jobs.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { Options, SelectedSlot } from './assisted-scheduler.interface';
import { TimeZoneDataType, TimezonePickerService } from 'app/core/services/timezone/timezone-picker.service';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { MatDatepicker } from '@angular/material/datepicker';
import _ from 'lodash';
import moment from 'moment';

const MAX_DURATION = 481;
@Component({
  selector: 'app-assisted-scheduler',
  templateUrl: './assisted-scheduler.component.html',
  styleUrls: ['./assisted-scheduler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistedSchedulerComponent implements OnInit {
  @ViewChild('selectDateRange') datePicker: MatDatepicker<Date>;
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const target = event?.target as HTMLElement;
    if (!target?.closest('.custom-dropdown-menu') && !target?.closest('.filter-search-clear')) {
      this.openMenusDropdown = null;
      this.searchQuery = '';
      this.filteredTimeZones = [...this.timeZoneData];
      this.filteredServiceTerritories = [...this.serviceTerritory];
      this.filteredSkills = [...this.skills];
    }
  }
  showAllUsers: boolean = false;
  selectedDate: any = moment();
  minDate: Date = new Date();
  workHours: any;
  teams: any = [];
  skills: any = [];
  filteredSkills: any = [];
  serviceTerritory: any = [];
  selectedTerritoriesMap: { [key: string]: boolean } = {};
  filteredServiceTerritories: any = [];
  showButtonSpinner: boolean;
  slotViewModes: any = [
    {
      type: 'RESOURCE',
      displayKey: 'scheduler.resource_view'
    },
    {
      type: 'SLOT',
      displayKey: 'scheduler.slot_view'
    }
  ];
  selectedViewMode: string = 'RESOURCE';
  allAvailableSlots: any = [];
  allUsers: any = {};
  resourceSlots: any = {};
  object = Object;
  isLoading: boolean = false;
  selectedSlot: SelectedSlot;
  inputOptions: Options;
  employeeSearchText: string;
  copyResourceSlots: any = {};
  dateFormat: string = this.utilityService.dateFormat;
  isSubmitted: boolean = false;
  considerOptions: any[] = [{
    optionKey: this.utilityService.getLocaleValue('yes', 'Yes'),
    optionValue: true
  }, {
    optionKey: this.utilityService.getLocaleValue('no', 'No'),
    optionValue: false
  }];
  timeZone: string = '';
  timezoneSubscription: Subscription;
  timeZoneData: Array<TimeZoneDataType> = [];
  selectedTeamsArr: any[] = [];
  assistedSchedulerSubscription: Subscription;
  duration = {
    hours: '0',
    mins: '0'
  }
  assistedScheduleModel = {
    considerUserShifts: false,
    teamsType: 'ANY',
  };
  holidays = [];
  loadingTeams: boolean = false;
  teamsCopyArr: any[] = [];
  currentStartIndex: number = 0;
  currentEndIndex: number = 5;
  currentMonth: string = '';
  weekDays: { dateValue: string; dayValue: string, date: any }[] = [];
  currentDate = moment();
  today: Date = new Date();
  openMenusDropdown: string | null = null;
  selectedTimezone: any;
  selectedSkill: any = null;
  selectedTerritories = [];
  fromTimeStepper: number = 15;
  searchQuery: string = '';
  filteredTimeZones: any[] = [];
  filteredTeams: any[] = [];
  searchTeam: string = '';
  toggleFilters: boolean = true;
  territoryDisplayText: string = '';
  private inputChangeSubject: Subject<void> = new Subject();
  isTimeZoneEnabled: boolean = true;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AssistedSchedulerComponent>,
    public utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private jobsService: JobsService,
    private _changeDetectorRef: ChangeDetectorRef,
    public timezoneService: TimezonePickerService
  ) {
    this.dateFormat = this.utilityService.dateFormat;
  }

  ngOnInit(): void {
    this.initializeData();
    this.setupSubscriptions();
  }

  initializeData(): void {
    this.inputOptions = this.data.data;
    this.isTimeZoneEnabled = this.utilityService.isTimeZoneEnabled
    this.workHours = this.inputOptions.workHours;
    this.setTimeFromWorkHours();
    this.timeZone = this.inputOptions.timeZone || this.utilityService.preferredTimeZone;
    moment.tz.setDefault(this.timeZone); // Ensure timezone is set before initializing dates
    this.minDate = moment.tz(moment(), this.timeZone).toDate();
    this.selectedDate = this.inputOptions.selectedDate
      ? moment.tz(moment(this.data.data?.selectedDate), this.timeZone)
      : moment.tz(moment(), this.timeZone);
    this.currentDate = moment.tz(moment(), this.timeZone);
    this.today = moment.tz(moment(), this.timeZone).toDate();
    if(this.inputOptions?.territory?.territory_uid) {
      this.onTerritorySelect(this.inputOptions?.territory);
    }
    else{
      this.getTeams();
    }
    this.getServiceTerritory();
    this.getSkills();
    this.getHolidays();
    this.callAssistedScheduleAPI();
  }

  setupSubscriptions(): void {
    this.timezoneSubscription = this.timezoneService.timeZones.subscribe((timezoneRes: TimeZoneDataType[]) => {
      if (timezoneRes) {
        this.timeZoneData = timezoneRes;
        this.selectedTimezone = this.timeZoneData.find((timezone) => timezone.key === this.timeZone);
        this.filteredTimeZones = [...this.timeZoneData];
      }
    });

    this.inputChangeSubject.pipe(debounceTime(1000)).subscribe(() => {
      this.callAssistedScheduleAPI();
    });

    if (!this.utilityService.isEmptyOrUndefined(this.inputOptions?.selectedSlotObj)) {
      this.setSelectedSlot(this.inputOptions.selectedSlotObj);
      this.callAssistedScheduleAPI();
    }
  }

  setSelectedSlot(selectedSlotObj: any): void {
    this.selectedTeamsArr = selectedSlotObj?.selectedTeamsArr;
    this.duration = selectedSlotObj?.duration;
    this.selectedTerritories = selectedSlotObj?.serviceTerritory;
    this.selectedSkill = selectedSlotObj?.skill || null;
    this.selectedViewMode = selectedSlotObj?.slotMode;
    this.selectedSlot = {
      startTime: selectedSlotObj?.startTime,
      endTime: selectedSlotObj?.endTime,
      userUid: selectedSlotObj?.userUid,
      teamUid: selectedSlotObj?.teamUid,
      user: selectedSlotObj?.user,
      team: selectedSlotObj?.team,
      timezone: selectedSlotObj?.timeZone
    };
    this.assistedScheduleModel.considerUserShifts = selectedSlotObj?.considerUserShifts;
    this.assistedScheduleModel.teamsType = selectedSlotObj?.teamsType;
    this.selectedDate  = selectedSlotObj?.selectedDate;
    this.initializeSelectedTerritoriesMap();
    this.updateTerritoryDisplayText();
  }

  getTeams(): void {
    this.loadingTeams = true;
    const params = {
      'count': 200,
      'page': 1,
      'filter.team_timezone': this.isTimeZoneEnabled ? this.timeZone : '',
      'filter.service_territory': this.selectedTerritories?.map((territory: any) => territory.territory_uid || '').join(',') || '',
      'filter.skillset_uid': this.selectedSkill?.skillset_uid || '',
    };
    this.dataCollectorService.getData(this.jobsService.jobAPIUrls.teamSummaryUrl, params)
      .subscribe((res) => {
        this.teams = res.data;
        this.filteredTeams = this.teamsCopyArr = _.cloneDeep(this.teams);
        this.loadingTeams = false;
        this.updateTeamsPermissions(this.selectedDate, this.teamsCopyArr);
        this._changeDetectorRef.detectChanges();
      }, (_err) => {
        this.loadingTeams = false;
        this.teams = this.filteredTeams = this.teamsCopyArr = [];
        this._changeDetectorRef.detectChanges();
      });
  }

  getSkills(): void {
    this.dataCollectorService.getData(this.jobsService.jobAPIUrls.skills)
      .subscribe((res) => {
        this.skills = res.data;
        this.filteredSkills = _.cloneDeep(this.skills)
        this._changeDetectorRef.detectChanges();
      }, (_err) => {
      });
  }

  getServiceTerritory(): void {
    const params = {
      'count': 100,
      'page': 1,
      'filter.is_active': true
    };
    this.dataCollectorService.getData(this.jobsService.jobAPIUrls.serviceTerritoryUrl, params)
      .subscribe((res) => {
        this.serviceTerritory = res.data;
        this.filteredServiceTerritories = _.cloneDeep(this.serviceTerritory);
        this._changeDetectorRef.detectChanges();
      }, (_err) => {
      });
  }

  callAssistedScheduleAPI(): void {
    this.selectedTeamsArr = this.selectedTeamsArr.filter(teamUid => {
      const holiday = this.holidays.find(h => h.team?.team_uid === teamUid && moment.utc(h.date).isSame(this.selectedDate, 'date') && !h.allow_job);
      return !holiday;
    });
    if (this.validateInputs()) {
      this.isLoading = false;
      if (this.assistedSchedulerSubscription) {
        this.assistedSchedulerSubscription?.unsubscribe();
      }
      this._changeDetectorRef.detectChanges();
      return;
    }
    if (this.assistedSchedulerSubscription) {
      this.assistedSchedulerSubscription?.unsubscribe();
    }
    this.isLoading = true;
    this._changeDetectorRef.detectChanges();
    this.allAvailableSlots = [];
    this.selectedSlot = null;
    let from_date;
    const selectedDate = this.selectedDate;
    const formattedSelectedDate = moment(selectedDate, this.dateFormat).format('YYYY-MM-DD');
    if (moment(selectedDate, this.dateFormat).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')) {
      from_date = this.roundToNextQuarterHour(moment()).format('YYYY-MM-DDTHH:mm:ss');
    } else {
      from_date = moment(selectedDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
    }
    let to_date;
    if (this.selectedViewMode === 'SLOT') {
      to_date = moment(selectedDate).add(2, 'days').endOf('day').format('YYYY-MM-DDTHH:mm:ss');
    } else {
      to_date = moment(selectedDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
    }
    const from_date_utc = moment(from_date, 'YYYY-MM-DDTHH:mm:ss').utc().format('YYYY-MM-DD HH:mm:ss');
    const to_date_utc = moment(to_date, 'YYYY-MM-DDTHH:mm:ss').utc().format('YYYY-MM-DD HH:mm:ss');
    const params = {
      'from_date': from_date_utc,
      'job_duration': this.getTotalDuration(),
      'service_territory': this.selectedTerritories?.map((territory: any) => territory.territory_uid || '').join(',') || '',
      'skillset_uid': this.selectedSkill?.skillset_uid || '',
      'team_uid': this.selectedTeamsArr?.join(',') || '',
      'to_date': to_date_utc,
      'group_by': (this.assistedScheduleModel.teamsType === 'SELECTED' && this.selectedTeamsArr?.length > 0) && this.selectedViewMode === 'RESOURCE' ? 'team' : '',
      'team_timezone':  this.isTimeZoneEnabled ? this.timeZone : ''
    };
    if (this.assistedScheduleModel.considerUserShifts) {
      params['consider_only_user_shifts'] = true;
    }
    this.assistedSchedulerSubscription = this.dataCollectorService.getData(this.jobsService.jobAPIUrls.assistedSchedulingUrl, params)
      .subscribe((res: any) => {
        this.resourceSlots = {};
        this.copyResourceSlots = {};
        this.isLoading = false;
        this.allAvailableSlots = res.data.availability;
        if (this.selectedViewMode === 'RESOURCE') {
          if (this.assistedScheduleModel.teamsType === 'SELECTED' && this.selectedTeamsArr.length > 0) {
            this.allUsers = {};
            this.allUsers = res?.data?.users;
            this.allAvailableSlots.forEach(team => {
              team['team_color'] = this.teams.find((teamObj) => teamObj.team_uid === team.team_uid)?.team_color;
              team?.users?.forEach(user => {
                user.slots = this.convertAndGroupSlotsWithTzResource(user.slots);
                user['userObj'] = this.allUsers.find((userObj) => userObj.user_uid === user.user_uid);
              });
              team.showMoreUsers = false;
            });

            this.copyResourceSlots = _.cloneDeep(this.allAvailableSlots);
          } else {
            this.allUsers = {};
            const convertedDateSlot = this.convertAndGroupSlotsWithTz(this.allAvailableSlots);
            const selectedDateSlot = [...convertedDateSlot].find((date: any) => date.date === moment(selectedDate).tz(this.timeZone).format('MM/DD/YYYY, ddd'));
            if (selectedDateSlot?.slots.length) {
              _.each(selectedDateSlot?.slots, (slot: any) => {
                _.each(slot.users, (user: string) => {
                  const obj = { start_time: slot.start_time, end_time: slot.end_time };
                  this.resourceSlots[user] = this.resourceSlots[user] ? [...this.resourceSlots[user], ...[obj]] : [obj];
                });
              });
              this.copyResourceSlots = _.cloneDeep(this.resourceSlots);
            }
            res.data.users.map((user: any) => {
              this.allUsers[user.user_uid] = user;
            });
          }
        } else {
          const slots = [...this.allAvailableSlots].filter((dates: any) => !moment(dates.date).isBefore(formattedSelectedDate) && !moment(dates.date).isAfter(to_date_utc));
          this.allAvailableSlots = this.convertAndGroupSlotsWithTz(slots);
          this.copyResourceSlots = _.cloneDeep(this.allAvailableSlots);
        }
        this._changeDetectorRef.detectChanges();
      }, (_err) => {
        this.isLoading = false;
        this._changeDetectorRef.detectChanges();
      });
  }

  convertAndGroupSlotsWithTzResource(slots: any): any {
    const tzConvertedSlots: any[] = [];
    const timezoneFormattedArray = slots?.map((slot) => {
      const start_time = moment.tz(moment.utc(slot.start_time), this.timeZone).format('YYYY-MM-DD HH:mm:ss');
      const end_time = moment.tz(moment.utc(slot.end_time), this.timeZone).format('YYYY-MM-DD HH:mm:ss');
      tzConvertedSlots.push({ ...slot, start_time, end_time });
      return { ...slot, start_time, end_time };
    });
    return timezoneFormattedArray;
  }

  convertAndGroupSlotsWithTz(slots: any): any {
    const tzConvertedSlots: any[] = [];
    const timezoneFormattedArray = slots?.map((item) => {
      const convertedDate = moment(item.date).format('YYYY-MM-DD');
      const convertedSlots = item?.slots.map((slot) => {
        const start_time = moment.tz(moment.utc(slot.start_time), this.timeZone).format('YYYY-MM-DD HH:mm:ss');
        const end_time = moment.tz(moment.utc(slot.end_time), this.timeZone).format('YYYY-MM-DD HH:mm:ss');
        tzConvertedSlots.push({ ...slot, start_time, end_time });
        return { ...slot, start_time, end_time };
      });
      return { ...item, date: convertedDate, slots: convertedSlots };
    });
    const slotsGroupedArray = timezoneFormattedArray.map((item) => {
      const filteredSlots = tzConvertedSlots.filter(
        filterItem => moment(item.date).isSame(moment(filterItem.start_time), 'day')
      );
      const formattedDate = moment(item.date).format('MM/DD/YYYY, ddd');
      let holiday = this.holidays.find(h => moment(h.date).startOf('day').isSame(moment(item.date).startOf('day'), 'date'));
      const currentDate = moment.tz(moment(this.selectedDate), this.timeZone).startOf('day');
      const isPastDate: boolean = currentDate.isAfter(moment(item.date));
      return { ...item, date: formattedDate, isPastDate: isPastDate, slots: filteredSlots,isHoliday: holiday && holiday?.all_users, allow_job: this.getAllowJobValue(holiday), holidayName: holiday?.holiday_name  };
    });
    return slotsGroupedArray;
  }

  roundToNextQuarterHour(time: moment.Moment): moment.Moment {
    const minutes = time.minutes();
    const remainder = minutes % 15;
    if (remainder === 0) {
      return time;
    }
    const adjustment = 15 - remainder;
    return time.add(adjustment, 'minutes');
  }

  changeViewMode(mode: string): void {
    if (this.slotViewModes !== mode) {
      this.employeeSearchText = '';
      this.selectedViewMode = mode;
      this.updateWeek(this.currentDate);
      this.updateTeamsPermissions(this.selectedDate,this.teamsCopyArr);
      this.callAssistedScheduleAPI();
    }
  }

  changeTeamsType(mode: string): void {
    this.employeeSearchText = '';
    this.searchTeam = '';
    this.assistedScheduleModel.teamsType = mode;
    this.selectedTeamsArr = [];
    this.getSlotsByTeamType();
  }

  setTabClasses(mode: string): any {
    return {
      'border-orange-500 text-gray-600': this.selectedViewMode === mode,
      'border-transparent text-gray-500 hover:text-gray-500 hover:border-orange-600': this.selectedViewMode !== mode
    };
  }

  selectSlot(user: any, slot: any, team: any): any {
    this.selectedSlot = {
      startTime: slot?.start_time,
      endTime: slot?.end_time,
      userUid: user?.user_uid || user,
      teamUid: team?.team_uid || '',
      user: user?.userObj || this.allUsers[user?.user_uid || user],
      team: this.teams.find((teamObj: any) => teamObj?.team_uid === team?.team_uid),
      timezone: this.timeZone,
      duration: this.duration,
      selectedTeamsArr: this.selectedTeamsArr,
      serviceTerritory: this.selectedTerritories,
      skill: this.selectedSkill,
      slotMode: this.selectedViewMode,
      considerUserShifts: this.assistedScheduleModel?.considerUserShifts,
      teamsType: this.assistedScheduleModel?.teamsType,
      selectedDate: this.selectedDate,
    };
  }

  emitSelectedSlot(): void {
    this.isSubmitted = true;
    if (this.selectedSlot) {
      this.dialogRef.close(this.selectedSlot);
    }
  }

  clearTeamAndUserSearch(): any {
    this.copyResourceSlots = _.cloneDeep(this.allAvailableSlots);
  }

  filterUsersAndTeams(): void {
    if (!this.employeeSearchText || this.employeeSearchText.trim() === '') {
      this.copyResourceSlots = _.cloneDeep(this.allAvailableSlots);
      return;
    }
    const lowerSearch = this.employeeSearchText?.toLowerCase();
    this.copyResourceSlots = this.allAvailableSlots
      .map(team => {
        const isTeamMatch = team?.team_name?.toLowerCase().includes(lowerSearch);
        if (isTeamMatch) {
          return { ...team, users: _.cloneDeep(team.users) };
        }
        const filteredUsers = team?.users?.filter(userObj => {
          const user = userObj?.userObj;
          const fullName = `${user?.first_name} ${user?.last_name}`.toLowerCase();

          return (
            fullName.includes(lowerSearch) ||
            user?.first_name?.toLowerCase().includes(lowerSearch) ||
            user?.last_name?.toLowerCase().includes(lowerSearch) ||
            user?.email?.toLowerCase().includes(lowerSearch)
          );
        });
        return filteredUsers.length > 0 ? { ...team, users: filteredUsers } : null;
      })
      .filter(team => team !== null);
  }

  updateWeek(currentDate: any): void {
    this.weekDays = [];
    let date = moment.tz(currentDate, this.timeZone).clone();
    this.filteredTeams = _.cloneDeep(this.teamsCopyArr);
    let slotViewCount = 0;
    while (this.weekDays.length < 5) {
      if (date.isBefore(moment.tz(this.today, this.timeZone), 'day')) {
        date.add(1, 'day');
        continue;
      }
      const formattedDate = date.format('YYYY-MM-DD');
      const holiday = this.holidays.find(h =>
        moment(h.date).startOf('day').isSame(moment.tz(formattedDate, this.timeZone).startOf('day'), 'date')
      );
      let isSlotView = false;
      if (this.selectedViewMode === 'SLOT' &&
        date.isSameOrAfter(moment.tz(this.selectedDate, this.timeZone), 'day') &&
        slotViewCount < 3) {
        isSlotView = true;
        slotViewCount++;
      } else {
        isSlotView = false;
      }
      const weekDayObj = {
        dateValue: date.format('DD'),
        dayValue: date.format('ddd'),
        date: date.clone(),
        isHoliday: holiday && holiday?.all_users,
        holidayName: holiday?.holiday_name,
        allow_job: this.getAllowJobValue(holiday),
        isSlotView: isSlotView,
      };
      this.weekDays.push(weekDayObj);
      date.add(1, 'day');
    }
    this.teams = _.cloneDeep(this.filteredTeams);
    this._changeDetectorRef.detectChanges();
  }

  prevWeek() {
    if (this.currentStartIndex > 0) {
      this.currentStartIndex -= 5;
      this.currentEndIndex -= 5;
    } else {
      // Load previous 5 days
      let newDate = this.currentDate.clone().subtract(5, 'days');
      if (newDate.isBefore(this.today, 'day')) {
        return; // Prevent navigating to past dates
      }
      this.currentDate = newDate;
      this.updateWeek(this.currentDate);
      this.currentStartIndex = 0;
      this.currentEndIndex = 5;
    }
    this._changeDetectorRef.detectChanges();
  }

  nextWeek() {
    this.currentDate = this.currentDate.add(5, 'days');
    this.updateWeek(this.currentDate);
    this.currentStartIndex = 0;
    this.currentEndIndex = 5;
    this._changeDetectorRef.detectChanges();
  }

  getAllowJobValue(holiday: any): boolean {
    if (!holiday?.allow_job && holiday?.all_users) {
      return false;
    } else {
      return true;
    }
  }

  handleDateClick(obj: any): void {
    if (obj?.all_users && !obj?.allow_job) {
      this.selectDate(obj);
    }
  }

  selectDate(obj: any) {
    this.employeeSearchText = '';
    this.searchTeam = '';
    this.copyResourceSlots = [];
    this.allAvailableSlots = []; 
    this.resourceSlots = [];
    this.selectedDate = moment(obj.date);
    this.updateWeek(this.currentDate);
    this.updateTeamsPermissions(this.selectedDate, this.teamsCopyArr);
    this.callAssistedScheduleAPI();
  }

  updateTeamsPermissions(selectedDate: moment.Moment, teamsArr): void {
    this.filteredTeams = _.cloneDeep(teamsArr);
    selectedDate = moment.tz(selectedDate, this.timeZone).startOf('day'); // Ensure selectedDate is timezone-aware
    let holidays = this.holidays.filter(h => moment(h.date).startOf('day').isSame(selectedDate.startOf('day'), 'date'));

    this.filteredTeams.forEach(team => {
      let allowJob = true;
      holidays.forEach(holiday => {
        if (team.team_uid === holiday?.team?.team_uid) {
          if (!holiday?.allow_job) {
            allowJob = false;
            team.holidayName = holiday?.holiday_name;
            team.holidayDate = holiday?.date;
          }
          team.isHoliday = true;
        }
      });
      team.not_allow_job = !allowJob;
    });
  }

  getMonthYear(): string {
    return this.currentDate.format('MMMM YYYY');
  }

  onTimezoneSelect(timezone) {
    this.selectedTeamsArr = [];
    this.searchQuery = '';
    this.searchTeam = '';
    this.copyResourceSlots = [];
    this.allAvailableSlots = [];
    this.resourceSlots = [];
    this.selectedTimezone = timezone;
    this.openMenusDropdown = null;
    this.timeZone = timezone?.key;
    moment.tz.setDefault(this.timeZone);
    this.selectedDate = moment.tz(moment(), this.timeZone);
    this.minDate = moment.tz(moment(), this.timeZone).toDate();
    this.updateWeek(this.currentDate);
    this.callAssistedScheduleAPI();
    this.getTeams();
  }

  toggleDropdown(menu: string): void {
    this.openMenusDropdown = this.openMenusDropdown === menu ? null : menu;
  }

  onSkillSelect(skill) {
    this.selectedSkill = skill;
    this.openMenusDropdown = null;
    this.selectedTeamsArr = [];
    this.searchQuery = '';
    this.searchTeam = '';
    this.copyResourceSlots = [];
    this.allAvailableSlots = [];
    this.resourceSlots = [];
    this.getTeams();
    this.callAssistedScheduleAPI();
  }

  removeSkillset() {
    this.selectedSkill = null;
    this.selectedTeamsArr = [];
    this.searchQuery = '';
    this.searchTeam = '';
    this.copyResourceSlots = [];
    this.allAvailableSlots = [];
    this.resourceSlots = [];
    this.getTeams();
    this.callAssistedScheduleAPI();
  }

  removeAllTerritories() {
    this.openMenusDropdown = null;
    this.selectedTerritories = [];
    this.territoryDisplayText = '';
    this.callAssistedScheduleAPI();
  }

  removeAllTeams() {
    this.openMenusDropdown = null;
    this.selectedTeamsArr = [];
    this.copyResourceSlots = [];
    this.allAvailableSlots = [];
    this.resourceSlots = [];
    this.callAssistedScheduleAPI();
  }

  onTerritorySelect(territory: any) {
    const index = this.selectedTerritories?.findIndex((t: any) => t?.territory_uid === territory?.territory_uid);

    if (index === -1) {
      this.selectedTerritories?.push(territory);
      this.selectedTerritoriesMap[territory.territory_uid] = true;
    } else {
      this.selectedTerritories?.splice(index, 1);
      delete this.selectedTerritoriesMap[territory.territory_uid];
    }
    this.updateTerritoryDisplayText();
    this._changeDetectorRef.detectChanges();
    this.selectedTeamsArr = [];
    this.searchQuery = '';
    this.searchTeam = '';
    this.copyResourceSlots = [];
    this.allAvailableSlots = [];
    this.resourceSlots = [];
    this.getTeams();
    this.callAssistedScheduleAPI();
  }

  isTerritorySelected(territory: any): boolean {
    return this.selectedTerritories?.some((t: any) => t?.territory_uid === territory?.territory_uid);
  }

  openDatePicker(): void {
    this.datePicker.open();
  }

  onDateSelect(event): void {
    this.currentDate = moment(event.value);
    this.selectedDate = event.value;
    this.updateWeek(this.currentDate);
    this.callAssistedScheduleAPI();
  }

  filterTimeZones() {
    if (!this.searchQuery.trim()) {
      this.filteredTimeZones = [...this.timeZoneData];
      return;
    }

    const lowerSearch = this.searchQuery?.toLowerCase();
    this.filteredTimeZones = this.timeZoneData?.filter(timezone =>
      timezone?.display_name.toLowerCase().includes(lowerSearch)
    );
  }

  filterServiceTerritory() {
    if (!this.searchQuery.trim()) {
      this.filteredServiceTerritories = [...this.serviceTerritory];
      return;
    }

    const lowerSearch = this.searchQuery?.toLowerCase();
    this.filteredServiceTerritories = this.serviceTerritory?.filter(territory =>
      territory?.territory_name?.toLowerCase()?.includes(lowerSearch)
    );
  }

  filterTeams() {
    if (!this.searchTeam.trim()) {
      this.filteredTeams = [...this.teams];
      this.updateTeamsPermissions(this.selectedDate, this.filteredTeams);
      return;
    }

    const lowerSearch = this.searchTeam?.toLowerCase();
    this.filteredTeams = this.teams?.filter(team =>
      team?.team_name?.toLowerCase()?.includes(lowerSearch)
    );
    this.updateTeamsPermissions(this.selectedDate, this.filteredTeams);
  }

  clearTeamSearch() {
    this.searchTeam = '';
    this.filteredTeams = [...this.teams];
    this.updateTeamsPermissions(this.selectedDate, this.filteredTeams);
  }

  filterSkills() {
    if (!this.searchQuery.trim()) {
      this.filteredSkills = [...this.skills];
      return;
    }

    const lowerSearch = this.searchQuery?.toLowerCase();
    this.filteredSkills = this.skills?.filter(skill =>
      skill?.skillset_name?.toLowerCase()?.includes(lowerSearch)
    );
    this._changeDetectorRef.detectChanges();
  }

  clearSearch() {
    this.searchQuery = '';
    this.filteredTimeZones = [...this.timeZoneData];
    this.filteredServiceTerritories = [...this.serviceTerritory];
    this.filteredSkills = [...this.skills];
  }

  getFilterClass(): any {
    return {
      'border-blue-500': this.selectedTerritories.length > 0,
      'bg-blue-50': this.selectedTerritories.length > 0,
      'text-gray-500': this.selectedTerritories.length === 0,
      'hover:border-gray-300': true,
    };
  }

  selectTeam(team: any) {
    const index = this.selectedTeamsArr?.indexOf(team.team_uid);
    if (index > -1) {
      this.selectedTeamsArr?.splice(index, 1);
    } else {
      this.selectedTeamsArr?.push(team.team_uid);
    }
    if (this.selectedTeamsArr.length === 0) {
      this.selectedTeamsArr = [];
      this.copyResourceSlots = [];
      this.allAvailableSlots = [];
      this.resourceSlots = [];
    }
    this.inputChangeSubject.next();
  }

  toggleTeamSelection(team: any, checkbox: HTMLInputElement): void {
    checkbox.checked = !checkbox.checked;
    this.selectTeam(team);
  }

  toggleShowMore(team: any) {
    team.showMoreUsers = !team.showMoreUsers;
  }

  unSelectAllTeams(): any {
    this.selectedTeamsArr = [];
    this.copyResourceSlots = [];
    this.allAvailableSlots = [];
    this.resourceSlots = [];
  }

  setTimeFromWorkHours() {
    if (this.workHours > MAX_DURATION || this.workHours === 0) {
      this.duration.hours = "1";
      this.duration.mins = '0';
    } else {
      this.duration.hours = Math.floor(this.workHours / 60).toString();
      this.duration.mins = (this.workHours % 60).toString();
    }
  }

  validateInputs() {
    if (this.getTotalDuration() > MAX_DURATION || this.getTotalDuration() === 0) {
      return true;
    }
    if (this.assistedScheduleModel.teamsType === 'SELECTED' && this.selectedTeamsArr.length === 0) {
      return true;
    }
    return false;
  }

  allowOnlyHours(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;

    if (charCode < 48 || charCode > 57 || (input.value.length >= 1 && selectionStart === selectionEnd)) {
      event.preventDefault();
    }
  }

  allowOnlyMinutes(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;
    const newValue = input.value.substring(0, selectionStart) + String.fromCharCode(charCode) + input.value.substring(selectionEnd);

    if (charCode < 48 || charCode > 57 || (input.value.length >= 2 && selectionStart === selectionEnd)) {
      event.preventDefault();
    }

    if (newValue.length === 2 && parseInt(newValue, 10) >= 60) {
      event.preventDefault();
    }
  }

  parseNumber(value: string): number {
    return parseInt(value, 10) || 0;
  }

  getTotalDuration() {
    let h = this.parseNumber(this.duration.hours);
    let m = this.parseNumber(this.duration.mins);

    return (h * 60) + m;
  }

  onInputChange() {
    this.inputChangeSubject.next();
  }

  isSelectedDate(date: moment.Moment): boolean {
    return date.isSame(this.selectedDate, 'day');
  }

  employeeSearchChange(): void {
    const users = [...(Object.keys(this.allUsers))].filter((data: any) => ((this.allUsers[data].first_name).toLowerCase().includes((this.employeeSearchText).toLowerCase()) || this.allUsers[data].last_name.toLowerCase().includes((this.employeeSearchText).toLowerCase()) || this.allUsers[data].email.toLowerCase().includes((this.employeeSearchText).toLowerCase())));
    this.copyResourceSlots = {};
    [...Object.keys(this.resourceSlots)].forEach((uid: string) => {
      if (users.includes(uid)) {
        this.copyResourceSlots[uid] = this.resourceSlots[uid];
      };
    });
    this._changeDetectorRef.detectChanges();
  }

  assignSlotViewData(): void {
    this.copyResourceSlots = _.cloneDeep(this.resourceSlots);
  }

  getSlotsByTeamType(): any {
    this.copyResourceSlots = [];
    this.allAvailableSlots = [];
    this.resourceSlots = [];
    this.clearTeamSearch();
    this.updateTeamsPermissions(this.selectedDate, this.teamsCopyArr);  
    this.callAssistedScheduleAPI();
  }

  getHolidays(): void {
    const params = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      limit: 100, sort: 'DESC', sort_by: 'created_at'
    };
    this.dataCollectorService.getData(this.jobsService.holidayUrl, params)
      .subscribe((response: any) => {
        this.holidays = response.data;
        this.updateWeek(this.today);
      });
  }

  setDefaultValue(field: string): void {
    if (field === 'hours' && !this.duration.hours) {
      this.duration.hours = '0';
    } else if (field === 'mins' && !this.duration.mins) {
      this.duration.mins = '0';
    }
  }

  initializeSelectedTerritoriesMap() {
    this.selectedTerritories.forEach(territory => {
      this.selectedTerritoriesMap[territory.territory_uid] = this.isTerritorySelected(territory);
    });
  }

  updateTerritoryDisplayText(): void {
    const maxLength = 40;
    let displayText = '';
    let remainingCount = 0;

    for (let i = 0; i < this.selectedTerritories?.length; i++) {
      const territoryName = this.selectedTerritories[i]?.territory_name;
      if ((displayText + territoryName).length > maxLength) {
        remainingCount = this.selectedTerritories?.length - i;
        break;
      }
      displayText += (displayText ? ', ' : '') + territoryName;
    }

    if (remainingCount > 0) {
      displayText += ` +${remainingCount}`;
    }

    this.territoryDisplayText = displayText;
    this._changeDetectorRef.markForCheck();
  }
}
